import {
  PinyinKeyboardKeys,
  type PinyinKeyboardKeysHandle,
} from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-keyboard.__components.keyboard";
import { toneNumberSyllableForComparison } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { cn } from "@-/com.npmjs.cn";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type Ref } from "react";
import type { LayoutChangeEvent, StyleProp, ViewStyle } from "@-/com.npmjs.react-native";
import { View } from "@-/com.npmjs.react-native";
import {
  getPinyinKeyboardKeyHeight,
  getPinyinKeyboardKeyWidth,
  KEY_GAP,
  PINYIN_KEYBOARD_HORIZONTAL_GUTTER,
  PINYIN_KEYBOARD_ROW_LIST,
} from "@-/dev.improve.haniverse.routes.nav.app._components.pinyin-keyboard._layout";
import Animated, {
  Easing,
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from "@-/com.npmjs.react-native-reanimated";

type KeyboardMode = "shuangpin" | "qwerty";

const SLIDE_MS = 90;
const slideTiming = { duration: SLIDE_MS, easing: Easing.linear } as const;

export type PinyinKeyboardHandle = PinyinKeyboardKeysHandle;

export function PinyinKeyboard({
  backspaceBehavior = "deleteLastCharacter",
  className,
  correctPinyinList,
  disabled = false,
  edgeInset = false,
  hideToneKeys = false,
  isVisible = true,
  layout = "shuangpin",
  onCheckFailed,
  onCheckPassed,
  onPendingInitialChange,
  onPinyinSelect,
  pinyinPreview,
  ref,
  resizeOnToggle = false,
  style,
  toggleButton,
  validPinyinSet,
}: {
  backspaceBehavior?: "deleteLastCharacter" | "clearInput";
  className?: string;
  correctPinyinList?: readonly string[];
  disabled?: boolean;
  /**
   * Inset the keys from the host’s left, right and bottom edges by `KEY_GAP`, so the edge spacing
   * matches the spacing between adjacent keys (for hosts like the drill card, whose edges the keys
   * would otherwise touch).
   */
  edgeInset?: boolean;
  /** Tone submit controls are provided by the host input row. */
  hideToneKeys?: boolean;
  /**
   * When false, slides the panel down by its laid-out height (same duration/easing as slide-in).
   * Keep the component mounted while toggling so the close animation can run.
   */
  isVisible?: boolean;
  /** Keyboard layout. Driven by the caller (e.g. the actor's `pinyinKeyboardLayout`). */
  layout?: KeyboardMode;
  onCheckFailed?: (value: string) => void;
  onCheckPassed?: (value: string) => void;
  onPendingInitialChange?: (initial: string | null) => void;
  onPinyinSelect: (pinyin: string) => void;
  /** Live preview string the keys read for tone/backspace editing. */
  pinyinPreview?: string;
  ref?: Ref<PinyinKeyboardHandle>;
  /** Release collapsed space and resize the prompt above the sliding keyboard. */
  resizeOnToggle?: boolean;
  /** Merged onto the outer animated wrapper (e.g. relative positioning in the footer). */
  style?: StyleProp<ViewStyle>;
  /** Rendered in the footer column directly above the pinyin preview field. */
  toggleButton?: ReactNode;
  validPinyinSet?: ReadonlySet<string>;
}) {
  const isQwertyMode = layout === "qwerty";

  const [containerWidth, setContainerWidth] = useState(0);
  // Without `edgeInset`, the edge inset (`px-1`) is owned by the host (e.g. `AboveKeyboard`); size
  // keys to the full laid-out width so they align with sibling rows like the pinyin input box.
  const edgeInsetPx = edgeInset ? KEY_GAP : 0;
  const innerWidth = Math.max(containerWidth - edgeInsetPx * 2, 0);
  // A collapsed viewport cannot supply an intrinsic height. Use the keys' own row metrics
  // so the first expansion works without laying out an invisible full-height keyboard.
  const intrinsicKeysHeight =
    innerWidth > 0
      ? PINYIN_KEYBOARD_ROW_LIST.length *
          getPinyinKeyboardKeyHeight(
            getPinyinKeyboardKeyWidth(
              innerWidth + PINYIN_KEYBOARD_HORIZONTAL_GUTTER * 2,
              !hideToneKeys && pinyinPreview !== undefined,
            ),
          ) +
        (PINYIN_KEYBOARD_ROW_LIST.length - 1) * KEY_GAP +
        edgeInsetPx
      : 0;
  const expansion = useSharedValue(isVisible ? 1 : 0);
  useEffect(() => {
    expansion.set(withTiming(isVisible ? 1 : 0, slideTiming));
  }, [expansion, isVisible]);

  // Compare on the tone-number form (keeping the tone) so the answer is only accepted when the
  // tone matches too — a plain syllable without the right tone (e.g. neutral tone 0) no longer
  // auto-passes, forcing the learner to mark the tone explicitly.
  const correctPinyinSet = useMemo(
    () =>
      new Set((correctPinyinList ?? []).map((pinyin) => toneNumberSyllableForComparison(pinyin))),
    [correctPinyinList],
  );

  const runCheck = useCallback(
    (rawValue: string) => {
      const trimmedValue = rawValue.trim();
      if (disabled || trimmedValue.length === 0) {
        return;
      }
      const normalizedValue = toneNumberSyllableForComparison(trimmedValue);
      if (correctPinyinSet.has(normalizedValue)) {
        onCheckPassed?.(trimmedValue);
        return;
      }
      onCheckFailed?.(trimmedValue);
    },
    [correctPinyinSet, disabled, onCheckFailed, onCheckPassed],
  );

  const handleTonePressed = useCallback(
    (resultingPinyin: string) => {
      if (isQwertyMode) {
        return;
      }
      runCheck(resultingPinyin);
    },
    [isQwertyMode, runCheck],
  );

  // Card hosts release the keyboard's space as it slides closed so the prompt can grow.
  // Other hosts retain their footprint while translating the keys behind their input row.
  const [keysHeight, setKeysHeight] = useState(0);
  const keysHeightSv = useSharedValue(0);
  const translateY = useSharedValue(isVisible ? 0 : 10_000);
  const isVisibleSv = useSharedValue(isVisible);
  const hasMeasuredSv = useSharedValue(false);
  const previousIsVisibleRef = useRef(isVisible);

  const handleKeysLayout = useCallback(
    (e: LayoutChangeEvent) => {
      const h = Math.round(e.nativeEvent.layout.height);
      // Ignore zero-height readings during initial layout or while an ancestor is hidden.
      // Writing those into `keysHeight` would make the re-show effect's `keysHeight <= 0`
      // guard bail. Keep the last real measurement instead.
      if (h <= 0) {
        return;
      }
      keysHeightSv.set(h);
      // Snap translateY in lockstep with keysHeightSv on the UI thread so the
      // animated style never sees a stale translateY against a fresh height
      // (which would briefly show the keys near their visible position with a
      // partial opacity — the "flash" on tab re-focus).
      if (!isVisibleSv.get()) {
        translateY.set(h);
      }
      hasMeasuredSv.set(true);
      setKeysHeight((prev) => (prev === h ? prev : h));
    },
    [keysHeightSv, translateY, isVisibleSv, hasMeasuredSv],
  );

  const handleContainerLayout = useCallback((e: LayoutChangeEvent) => {
    const w = Math.round(e.nativeEvent.layout.width);
    setContainerWidth((prev) => (prev === w ? prev : w));
  }, []);

  useEffect(() => {
    isVisibleSv.set(isVisible);
    if (keysHeight <= 0) {
      return;
    }
    const visibilityChanged = previousIsVisibleRef.current !== isVisible;
    previousIsVisibleRef.current = isVisible;
    const translateTarget = isVisible ? 0 : keysHeight;
    if (visibilityChanged) {
      translateY.set(withTiming(translateTarget, slideTiming));
    } else {
      translateY.set(translateTarget);
    }
  }, [isVisible, keysHeight, translateY, isVisibleSv]);

  const keysSlideStyle = useAnimatedStyle(() => {
    if (resizeOnToggle) {
      // Growing upward from the card's bottom also moves the prompt above the keyboard.
      return { height: intrinsicKeysHeight * expansion.get(), opacity: expansion.get() };
    }
    const h = keysHeightSv.get();
    if (!hasMeasuredSv.get()) {
      // Before first measurement, force opacity to match intended visibility so
      // we never render the keys at opacity 1 while translateY hasn't been
      // reconciled with the measured height.
      return {
        height: isVisibleSv.get() ? undefined : 0,
        opacity: isVisibleSv.get() ? 1 : 0,
        transform: [{ translateY: translateY.get() }],
      };
    }
    // Non-resizing hosts keep their footprint while the keys translate behind the input.
    return {
      height: h,
      opacity: h <= 0.5 ? 1 : interpolate(translateY.get(), [0, h], [1, 0], Extrapolation.CLAMP),
      transform: [{ translateY: translateY.get() }],
    };
  });

  return (
    <View className="relative">
      <View
        className={cn("relative flex flex-col bg-background", className)}
        onLayout={handleContainerLayout}
        style={style}
      >
        {innerWidth > 0 ? (
          <Animated.View
            accessibilityElementsHidden={!isVisible}
            className="relative overflow-hidden"
            importantForAccessibility={isVisible ? "auto" : "no-hide-descendants"}
            pointerEvents={isVisible ? "auto" : "none"}
            style={keysSlideStyle}
          >
            {/* `px-1 pb-1` matches `KEY_GAP` (the `gap-1` between keys). */}
            <View
              className={cn(edgeInset && "px-1 pb-1")}
              onLayout={handleKeysLayout}
              style={resizeOnToggle ? { height: intrinsicKeysHeight } : undefined}
            >
              {/* Unpadded containing block so the absolutely positioned toggle aligns with the keys. */}
              <View>
                <PinyinKeyboardKeys
                  backspaceBehavior={backspaceBehavior}
                  disabled={disabled}
                  hidePinyinHints={isQwertyMode}
                  hideToneKeys={hideToneKeys}
                  innerWidth={innerWidth}
                  onPendingInitialChange={onPendingInitialChange}
                  onPinyinSelect={onPinyinSelect}
                  onTonePressed={handleTonePressed}
                  pinyinPreview={pinyinPreview}
                  ref={ref}
                  shuangpinEnabled={!isQwertyMode}
                  swipeEnabled={!isQwertyMode}
                  validPinyinSet={validPinyinSet}
                />
                {resizeOnToggle ? toggleButton : null}
              </View>
            </View>
          </Animated.View>
        ) : null}
      </View>
      {/*
        Rendered outside the sliding keys wrapper so dismissing the keyboard slides only the keys
        down behind a toggle button that stays put. The toggle is absolutely positioned
        (bottom-left of the keys) by its own wrapper.
      */}
      {resizeOnToggle ? null : toggleButton}
    </View>
  );
}
